<template>
  <div id="pallet-top">
    <canvas
      id="draggable"
      :class="{ cursor: showCursor }"
      v-if="pallet"
      @dblclick="editPallet($event)"
      @mousemove="mouseMoved($event)"
      @mousedown="move($event)"
      @contextmenu="showMenu"
    ></canvas>

    <v-menu v-model="menu" :position-x="x" :position-y="y" absolute offset-y>
      <v-list>
        <v-list-tile class="clickable" @click="stackOnTop">
          <v-list-tile-title>{{ $t('pallet_top_drag.stack_on_top') }}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" v-if="!selectedStacked" @click="flip">
          <v-list-tile-title>{{ $t('pallet_top_drag.flip') }}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile
          class="clickable"
          @click="$refs.confirmBox.openDialog($t('pallet_top_drag.put_away_confirm'), 'putAway')"
        >
          <v-list-tile-title>{{ $t('pallet_top_drag.put_away') }}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" v-if="selectedStacked" @click="moveDown">
          <v-list-tile-title>{{ $t('pallet_top_drag.move_down') }}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" @click="moveToUpperRow">
          <v-list-tile-title>{{$t('pallet_top_drag.upper_row_end')}}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" @click="openDragToMiddle">
          <v-list-tile-title>{{$t('pallet_top_drag.drag_to_middle')}}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" v-if="!selectedStacked" @click="changeParameters">
          <v-list-tile-title>{{ $t('pallet_top_drag.change_parameters') }}</v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-menu>

    <pallet-structure-editor ref="structureEditor" @updated="$emit('updated')" />
    <pallet-dimensions-editor ref="dimensionsEditor" @updated="$emit('updated')" />
    <ConfirmBox ref="confirmBox" @confirmDialog="dialogConfirmed" />
    <ChangeProductParameters ref="productParams" @productUpdated="updateProduct" />
    <DragToMiddleDialog ref="dragToMiddleDialog" @productUpdated="dragedToMiddle" />
  </div>
</template>

<script>
import Pallet from "@/models/Pallet.js";
import PalletTop from "@/models/PalletTop.js";
import ConfirmBox from "@/components/ConfirmBox.vue";
import ChangeProductParameters from "@/components/palleting/ChangeProductParameters.vue";
import PalletStructureEditor from "@/components/palleting/PalletStructureEdit.vue";
import PalletDimensionsEditor from "@/components/palleting/PalletDimensions.vue";
import DragToMiddleDialog from "@/components/palleting/DragToMiddleDialog.vue";
import Helper from "@/models/Helper.js";
export default {
  name: "pallet-top",
  props: ["divider", "selectedPallet", "layers", "user"],
  data() {
    return {
      palletTop: null,
      pallet: null,
      showCursor: false,
      menu: false,
      x: 0,
      y: 0,
      selectedStacked: false,
      //used for selecting product
      selectedProduct: null,
      mouseIsOver: -1
    };
  },
  mounted() {
    this.initialize();
    document.addEventListener("moved", () => {
      this.$store.commit('setLoading', true);
      this.$emit("updated");
    });
    document.addEventListener("layerEdit", e => {
      if (this.$refs.structureEditor) {
        this.$refs.structureEditor.openDialog(this.pallet, e.detail);
      }
    });
    document.addEventListener("palletEdit", e => {
      if (this.$refs.dimensionsEditor) {
        this.$refs.dimensionsEditor.openDialog(
          this.pallet,
          e.detail,
          this.divider
        );
      }
    });
  },
  watch: {
    selectedPallet() {
      this.getPallet();
    },
    divider() {
      this.getPallet();
    },
    layers: {
      handler() {
        this.getPallet();
      },
      deep: true
    }
  },
  methods: {
    async initialize() {
      await this.getPallet();
      const canvas = document.getElementById("draggable");
      canvas.width = this.pallet.length;
      canvas.height = this.pallet.width;
      const ctx = canvas.getContext("2d");
      this.palletTop = new PalletTop(canvas, ctx, this.pallet, this.layers);
    },

    async getPallet() {
      const pallet = new Pallet(this.selectedPallet);
      this.pallet = pallet.prepareForViewing(this.divider);
      if (this.palletTop) {
        this.palletTop.initialize(this.pallet, this.layers);
      }
    },

    mouseMoved(event) {
      if (!this.pallet.owned) return;
      const overProduct = this.palletTop.overProduct(event);
      if (overProduct != null) {
        overProduct.products[0].select(this);
      } else {
        this.selectedProduct = null;
        this.mouseIsOver = -1;
      }
      if (this.pallet.truck_id != null) return;
      if(this.user.type == 6) return;
      if (this.pallet.status > 1) return;
      this.showCursor = overProduct != null ? true : false;
    },

    move(event) {
      if(this.user.type == 6) return;
      if (this.pallet.truck_id != null) return;
      //make sure using left click
      if (event.button != 0) return;
      if (this.palletTop == null) return;
      if (!this.pallet.owned) return;
      this.palletTop.grab(event);
    },

    async showMenu(e) {
      e.preventDefault();
      if (this.pallet.truck_id != null) return;
      if(this.user.type == 6) return;
      if (this.pallet.status > 1) return;
      if (!this.pallet.owned) return;
      this.selectedStacked = false;
      this.selectedStack = this.palletTop.overProduct(e);
      if (!this.selectedStack) return;
      this.selectedStacked = this.selectedStack.products.length > 1;
      this.menu = false;
      this.x = e.clientX;
      this.y = e.clientY;
      this.$nextTick(() => {
        this.menu = true;
      });
      await Helper.timeout(500);
      this.$emit("hideProduct");
    },

    dialogConfirmed(action) {
      this[action]();
    },

    putAway() {
      this.$store.commit('setLoading', true);
      this.selectedStack.putAway();
      this.palletTop.draw();
      this.palletTop.drop();
    },

    async stackOnTop() {
      this.$store.commit('setLoading', true);
      this.palletTop.stackOnTop(this.selectedStack);
    },

    moveDown() {
      this.$store.commit('setLoading', true);
      this.palletTop.moveDown(this.selectedStack);
    },

    flip() {
      if(!this.selectedStack.getCanBeFlipped()){
        alert(this.$t('pallet_top.can_not_be_flipped'));
        return
      }
      this.$store.commit('setLoading', true);
      this.selectedStack.flip();
      this.palletTop.drop();
    },

    changeParameters() {
      this.$refs.productParams.open(this.selectedStack);
    },

    async updateProduct() {
      this.$emit("updated");
      await Helper.timeout(500);
      this.palletTop.drop();
    },

    dragedToMiddle() {
      this.$store.commit('setLoading', true);
      this.palletTop.draw();
      this.$emit("updated");
    },

    editPallet(event) {
      if (!this.pallet.owned) return;
      if (this.pallet.truck_id != null) return;
      if(this.user.type == 6) return;
      if (this.pallet.status > 1) return;
      this.palletTop.openDialogMenu(event);
    },

    moveToUpperRow() {
      this.$store.commit('setLoading', true);
      this.palletTop.upperRow(this.selectedStack);
      this.palletTop.drop();
    },

    openDragToMiddle() {
      this.$refs.dragToMiddleDialog.open(this.pallet, this.selectedStack);
    }
  },
  components: {
    ConfirmBox,
    ChangeProductParameters,
    PalletStructureEditor,
    PalletDimensionsEditor,
    DragToMiddleDialog
  }
};
</script>

<style>
.cursor {
  cursor: move;
}
.clickable {
  cursor: pointer;
}
</style>
